import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Loader, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Routes } from '@/_shared/constants/routes';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDepartmentTeamsCall } from '@/departments/state';
import { getEmployeesMeCall } from '../../state';

export const TeamsDepartment = () => {
  const t = useTranslations('profile.teamList');
  const tShared = useTranslations();
  const navigate = useNavigate();
  const { genericResponsiveValue } = useResponsiveStyle();

  const { leadership } = getEmployeesMeCall.hook();

  const {
    execute: executeGetDepartmentTeams,
    loading: loadingGetDepartmentTeams,
    error: errorGetDepartmentTeams,
    response: responseGetDepartmentTeams,
  } = getDepartmentTeamsCall.hook();

  const goToDetails = () => {
    navigate(Routes.Department);
  };

  const goToTeam = (teamId: string) => {
    navigate(Routes.DepartmentTeamDetails.replace(':teamId', teamId));
  };

  const gapS = useThemeValue('space', 's');

  useEffect(() => {
    executeGetDepartmentTeams({
      departmentId: leadership?.department?.id || '',
    });
  }, [executeGetDepartmentTeams, leadership]);

  if (loadingGetDepartmentTeams) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetDepartmentTeams) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (responseGetDepartmentTeams && responseGetDepartmentTeams?.data?.length > 0) {
    return (
      <Box mt='l'>
        <Box>
          <Box
            justifyContent='space-between'
            alignItems={genericResponsiveValue({
              breakPoints: {
                desktop: 'center',
              },
              defaultValue: 'flex-start',
            })}
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: {
                desktop: 'row',
              },
              defaultValue: 'column',
            })}
            mt='m'
            mb='m'
          >
            <Box
              alignItems='center'
              display='flex'
              flexDirection='row'
              justifyContent={genericResponsiveValue({
                breakPoints: {
                  desktop: 'flex-start',
                },
                defaultValue: 'space-between',
              })}
              width={genericResponsiveValue({
                breakPoints: {
                  desktop: 'auto',
                },
                defaultValue: '100%',
              })}
            >
              <Typography mr='s' variant='h2'>
                {leadership?.department?.name}
              </Typography>
              <Box alignItems='center' flexDirection='row' display='flex'>
                <Svg name='users' size='s' />
                <Typography ml='xxs' variant='h1'>
                  {responseGetDepartmentTeams?.department.stats?.totalEmployees || 0}
                </Typography>
              </Box>
            </Box>
            <Box
              alignItems='center'
              flexDirection='row'
              display='flex'
              onClick={goToDetails}
              style={{ cursor: 'pointer' }}
              mt={genericResponsiveValue({
                breakPoints: {
                  desktop: 'none',
                },
                defaultValue: 'xs',
              })}
            >
              <Typography mr='xxxs' variant='p2/light'>
                {t('goTo', { count: responseGetDepartmentTeams?.data.length || 0 })}
              </Typography>
              <Svg name='arrowRight' size='xs' />
            </Box>
          </Box>
        </Box>

        <Box
          style={{ gap: gapS }}
          display='grid'
          gridTemplateColumns={genericResponsiveValue({
            breakPoints: {
              desktop: 'repeat(3, 1fr)',
              tablet: 'repeat(1, 1fr)',
              mobile: 'repeat(1, 1fr)',
            },
            defaultValue: 'repeat(1, 1fr)',
          })}
        >
          {responseGetDepartmentTeams?.data.slice(0, 3).map(team => (
            <Card key={team.name}>
              <Box
                alignItems='center'
                display='flex'
                flexDirection='row'
                justifyContent='space-between'
                mt='xxxs'
                borderBottom='1px solid'
                borderColor='grey/light/background'
                pb='xs'
              >
                <Typography variant='h4'>
                  {team.id !== 'unknown' ? team.name : t('noTeamName')}
                </Typography>
                <Box alignItems='center' flexDirection='row' display='flex'>
                  <Svg name='users' size='s' />
                  <Typography ml='xxs' variant='h1'>
                    {team.totalEmployees || 0}
                  </Typography>
                </Box>
              </Box>
              <Box display='flex' flexDirection='row' justifyContent='space-between' mt='xs'>
                <Typography variant='p3/label'>{t('manager')}</Typography>
                <Typography variant='p2/light' as='span'>
                  {`${team?.manager?.firstName ?? ''} ${team?.manager?.lastName ?? ''}`.trim() ||
                    '-'}
                </Typography>
              </Box>
              {team.id !== 'unknown' && (
                <Button
                  onClick={() => team && goToTeam(team.id)}
                  variant='positive'
                  mt='m'
                  fontSize='s'
                  fontWeight='500'
                >
                  {t('viewDetails')}
                </Button>
              )}
            </Card>
          ))}
        </Box>
      </Box>
    );
  }

  return null;
};
